import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { DatePicker } from "@/components/ui/date-picker"
import { FormField } from "@/components/ui/form"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { MultipleSelect } from "@/modules/patients/components/multiple-select"
import { RecordExtractFormValues } from "@/modules/patients/detail/record-extract/lib/record-extract-schema"
import {
  EpisodeOfCareOption,
  PROGRESS_NOTE_TYPE,
  ProgressNoteType,
} from "@/modules/patients/detail/record-extract/types/record-extract-types"

const TIME_WINDOW_SWITCH_ID = "record-extract-time-window"

const PROGRESS_NOTE_TYPE_OPTIONS: { value: ProgressNoteType; labelKey: string }[] = [
  { value: PROGRESS_NOTE_TYPE.all, labelKey: "patient.recordExtract.progressNoteType.all" },
  { value: PROGRESS_NOTE_TYPE.medical, labelKey: "patient.recordExtract.progressNoteType.medical" },
  { value: PROGRESS_NOTE_TYPE.administrative, labelKey: "patient.recordExtract.progressNoteType.administrative" },
]

const DATE_FIELDS = [
  { name: "startDate", labelKey: "patient.recordExtract.filters.start", testId: "record-extract-start-date" },
  { name: "endDate", labelKey: "patient.recordExtract.filters.end", testId: "record-extract-end-date" },
] as const

interface ExtractFiltersProps {
  episodeOfCareOptions: EpisodeOfCareOption[]
  disabled?: boolean
}

export const ExtractFilters = ({ episodeOfCareOptions, disabled }: ExtractFiltersProps) => {
  const { t } = useTranslation()
  const { control, trigger } = useFormContext<RecordExtractFormValues>()

  const getProgressNoteTypeLabel = (value: ProgressNoteType) =>
    t(PROGRESS_NOTE_TYPE_OPTIONS.find((option) => option.value === value)?.labelKey ?? "")

  const revalidateDateRange = () => void trigger(["startDate", "endDate"])

  return (
    <div className="flex flex-col gap-4">
      <p className="text-on-surface text-base font-medium">{t("patient.recordExtract.filters.title")}</p>

      <div className="flex gap-4">
        {DATE_FIELDS.map(({ name, labelKey, testId }) => (
          <FormField
            key={name}
            control={control}
            name={name}
            render={({ field, fieldState }) => (
              <div className="flex-1">
                <DatePicker
                  readOnly={false}
                  label={t(labelKey)}
                  value={field.value}
                  onSelect={(date) => {
                    field.onChange(date)
                    revalidateDateRange()
                  }}
                  onReset={() => {
                    field.onChange(undefined)
                    revalidateDateRange()
                  }}
                  disabled={disabled}
                  error={fieldState.error?.message}
                  required
                  translateError
                  dataTestId={testId}
                />
              </div>
            )}
          />
        ))}
      </div>

      <FormField
        control={control}
        name="progressNoteType"
        render={({ field: { value, onChange, ref } }) => (
          <Select value={value} onValueChange={onChange} disabled={disabled} {...ref}>
            <SelectTrigger data-testid="record-extract-progress-note-type">
              <SelectValue asChild>
                <LabelAndSelectValue
                  label={t("patient.recordExtract.progressNoteType.label")}
                  value={getProgressNoteTypeLabel(value)}
                />
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {PROGRESS_NOTE_TYPE_OPTIONS.map((option) => (
                <SelectItem
                  value={option.value}
                  key={option.value}
                  data-testid={`record-extract-progress-note-type-${option.value}`}>
                  {t(option.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />

      <FormField
        control={control}
        name="episodeOfCareIds"
        render={({ field: { value, onChange } }) => (
          <MultipleSelect<EpisodeOfCareOption>
            data-testid="record-extract-eoc"
            disabled={disabled}
            label={t("patient.recordExtract.episodeOfCare.label")}
            options={episodeOfCareOptions}
            value={episodeOfCareOptions.filter((option) => value.includes(option.id))}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.id}
            onChange={(selected) => onChange(selected.map((option) => option.id))}
          />
        )}
      />

      <FormField
        control={control}
        name="timeWindowEnabled"
        render={({ field }) => (
          <div className="text-on-surface flex items-center gap-2.5">
            <Switch
              id={TIME_WINDOW_SWITCH_ID}
              className="h-6 w-11 [&>span]:size-5 [&>span]:data-[state=checked]:translate-x-5.5"
              checked={field.value}
              disabled={disabled}
              onCheckedChange={field.onChange}
            />
            <label htmlFor={TIME_WINDOW_SWITCH_ID} className="cursor-pointer text-sm">
              {t("patient.recordExtract.timeWindow.label")}
            </label>
          </div>
        )}
      />
    </div>
  )
}
